import React from 'react';
import { Grid } from '@mui/material';
import {
    TextField,
    NumberField,
    OrgNumberField,
    DateField
} from '../../CommonComponents/Form';
import { SectionDivider, Popup } from '../../CommonComponents';
import SelectField from '../../CommonComponents/Form/fields/SelectField';
import { carrierNameCapitalized, nonCarrierNameCapitalized, nonCarrierName } from '../../../env';

type BasicInfoEntity = {
    name: string;
    orgNumber: string;
    isCarrier: boolean;
    subscriptionEndDate: string;
    activeSubscription: boolean;
    employees: number | null;
    revenue: number | null;
    numberOfTrucksOverride: number | null;
};

interface BasicInfoProps {
    entity: BasicInfoEntity;
    onChange: (e: Partial<BasicInfoEntity> | React.ChangeEvent<any>) => void;
}

function getSubscriptionTitle(
    { subscriptionEndDate, activeSubscription }
        : { subscriptionEndDate: string, activeSubscription: boolean }
) {
    const commonTitle = 'Abonnemang tecknat t.o.m.';
    if (activeSubscription) {
        return `${commonTitle} (aktivt)`;
    }
    if (subscriptionEndDate) {
        return `${commonTitle} (utlöpt)`;
    }
    return `${commonTitle} (aldrig tecknat)`;
}

const BasicInfoBlock = ({ entity, onChange }: BasicInfoProps) => {
    const [openPopup, setOpenPopup] = React.useState(false);

    const changeIsCarrier = React.useCallback((change: Partial<BasicInfoEntity>) => {
        if (entity.isCarrier && change.isCarrier === false) {
            setOpenPopup(true);
            return;
        }

        onChange(change);
    }, [entity.isCarrier, onChange]);

    return (
        <>
            <Popup
                open={openPopup}
                title="Ändra företagstyp"
                body={`Är du säker på att du vill ändra företagstypen till ${nonCarrierName}? All information om utrustning, certifikat, fordonstyper, hållbarhet, bevakningar och prenumerationer kommer att tas bort när du sparar.`}
                okLabel="Ja"
                cancelLabel="Nej"
                handleOk={() => {
                    onChange({ isCarrier: false, numberOfTrucksOverride: null });
                    setOpenPopup(false);
                }}
                handleClose={() => setOpenPopup(false)}
            />
            <Grid item lg={12} xl={6}>
                <SectionDivider label="Grunduppgifter" marginTop={2} marginBottom={0} />
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            name="name"
                            label="Namn"
                            value={entity.name}
                            onChange={onChange}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <OrgNumberField
                            name="orgNumber"
                            label="Org. nummer"
                            value={entity.orgNumber}
                            onChange={onChange}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SelectField
                            name="isCarrier"
                            label="Företagstyp"
                            value={entity.isCarrier}
                            options={[
                                { value: true, label: carrierNameCapitalized },
                                { value: false, label: nonCarrierNameCapitalized }
                            ]}
                            onChange={changeIsCarrier}
                            required
                        />
                    </Grid>
                    {entity.isCarrier && (
                        <Grid item xs={12}>
                            <DateField
                                name="subscriptionEndDate"
                                label={getSubscriptionTitle({
                                    subscriptionEndDate: entity.subscriptionEndDate,
                                    activeSubscription: entity.activeSubscription
                                })}
                                value={entity.subscriptionEndDate}
                                onChange={onChange}
                            />
                        </Grid>
                    )}
                    {entity.isCarrier && (
                        <Grid item xs={12}>
                            <NumberField
                                name="numberOfTrucksOverride"
                                label="Antal lastbilar"
                                value={entity.numberOfTrucksOverride?.toString() || ''}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <NumberField
                            name="employees"
                            label="Antal anställda"
                            value={entity.employees?.toString() || ''}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <NumberField
                            name="revenue"
                            label="Omsättning"
                            value={entity.revenue?.toString() || ''}
                            onChange={onChange}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default React.memo(BasicInfoBlock);
